import React, { useEffect, useState, useRef, useContext } from 'react';
import { PresenceContext } from '../App';
import 'ol/ol.css';
import { Map, View, Overlay } from 'ol';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Point from 'ol/geom/Point';
import Feature from 'ol/Feature';
import { Style, Fill, Stroke } from 'ol/style';
import Icon from 'ol/style/Icon';
import { defaults as defaultControls } from 'ol/control';
import ScaleLine from 'ol/control/ScaleLine';
import XYZ from 'ol/source/XYZ';
import { fromLonLat } from 'ol/proj';
import axios from 'axios';
import Papa from 'papaparse';
import Modal from 'react-bootstrap/Modal';
import './ConnectedUsers.css';
import RegularShape from 'ol/style/RegularShape';
import ConnectionHistory from './ConnectionHistory';
import { utils, writeFile } from 'xlsx';
import { toast } from 'react-hot-toast';

// Función auxiliar para evaluar la precisión de la localización
const getLocationPrecision = (location) => {
  if (!location) return 0;
  if (location.source === 'ip') return 1;
  if (location.accuracy) return 2;
  return 1.5;
};

// Modificar la función para comparar usuarios
const matchUsers = (geoipUser, firebaseUser) => {
  return firebaseUser.ip === geoipUser.ip && firebaseUser.online === true;
};

const ConnectedUsers = ({ radioname, serverid }) => {
  const { getUsers } = useContext(PresenceContext);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const mapInstance = useRef(null);
  const mapElement = useRef(null);
  const [vectorSource] = useState(() => new VectorSource());
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setHours(0,0,0,0)),
    end: new Date(new Date().setHours(23,59,59,999))
  });

  useEffect(() => {
    if (!mapInstance.current && mapElement.current) {
      const darkStyle = new TileLayer({
        source: new XYZ({
          url: 'https://cartodb-basemaps-{a-d}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png',
          attributions: '© CARTO'
        })
      });

      const vectorLayer = new VectorLayer({
        source: vectorSource,
        style: new Style({
          image: new Icon({
            src: `data:image/svg+xml;utf8,
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                <defs>
                  <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:%239400D3;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:%23a020f0;stop-opacity:0.8" />
                  </linearGradient>
                  <filter id="shadow">
                    <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="%23000" flood-opacity="0.3"/>
                  </filter>
                </defs>
                <path fill="url(%23grad)" filter="url(%23shadow)" 
                  d="M16 2C10.477 2 6 6.477 6 12c0 7 10 18 10 18s10-11 10-18c0-5.523-4.477-10-10-10zm0 6a4 4 0 110 8 4 4 0 010-8z"
                />
                <circle cx="16" cy="12" r="4" fill="white" opacity="0.9"/>
              </svg>`,
            scale: 0.8,
            anchor: [0.5, 1]
          })
        })
      });

      mapInstance.current = new Map({
        target: mapElement.current,
        layers: [darkStyle, vectorLayer],
        view: new View({
          center: fromLonLat([-102.5528, 23.6345]),
          zoom: 5,
          minZoom: 4,
          maxZoom: 18,
          extent: [-20026376.39, -20048966.10, 20026376.39, 20048966.10],
          constrainResolution: true
        }),
        controls: defaultControls({
          zoom: true,
          rotate: false,
          attribution: false
        }).extend([
          new ScaleLine({
            units: 'metric',
            bar: true,
            steps: 4,
            text: true,
            minWidth: 140
          })
        ])
      });

      const popup = new Overlay({
        element: document.createElement('div'),
        positioning: 'bottom-center',
        offset: [0, -20],
        className: 'user-popup'
      });

      mapInstance.current.addOverlay(popup);

      mapInstance.current.on('pointermove', (e) => {
        const feature = mapInstance.current.forEachFeatureAtPixel(e.pixel, f => f);
        
        if (feature) {
          const userData = feature.get('userData');
          const element = popup.getElement();
          element.innerHTML = `
            <div class="user-info-popup">
              <h2>${userData.clientName}</h2>
              <div class="info-details">
                <p><i class="fas fa-map-marker-alt"></i> ${userData.geodata?.city || 'Desconocido'}, ${userData.geodata?.region || ''}</p>
                <p><i class="fas fa-globe"></i> ${userData.geodata?.country_name === 'Mexico' ? 'México' : userData.geodata?.country_name || ''}</p>
                <p class="connection-time"><i class="fas fa-clock"></i> ${userData.connecttime}</p>
                <p class="ip-address"><i class="fas fa-network-wired"></i> ${userData.ip}</p>
              </div>
            </div>
          `;
          popup.setPosition(e.coordinate);
        } else {
          popup.setPosition(undefined);
        }
      });
    }
  }, [vectorSource]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const geoipData = await axios.get(`https://radio.amgroupmkt.com:1030/api/v2/listeners_geoip/?server=${serverid}`);
        const geoipUsers = geoipData.data;
        const firebaseUsers = await getUsers(radioname);

        if (!mapInstance.current) return;

        vectorSource.clear();
        const users = [];

        // Procesar usuarios de GeoIP
        geoipUsers.forEach(geoipUser => {
          // Buscar usuario de Firebase que coincida por IP
          const matchingFirebaseUser = firebaseUsers ? 
            Object.values(firebaseUsers).find(fbUser => matchUsers(geoipUser, fbUser)) : null;

          // Determinar qué localización usar
          let locationToUse;
          if (matchingFirebaseUser?.location) {
            const firebaseLocationPrecision = getLocationPrecision(matchingFirebaseUser.location);
            const geoipLocationPrecision = getLocationPrecision({
              latitude: geoipUser.geodata.latitude,
              longitude: geoipUser.geodata.longitude
            });

            locationToUse = firebaseLocationPrecision >= geoipLocationPrecision ?
              matchingFirebaseUser.location :
              {
                latitude: geoipUser.geodata.latitude,
                longitude: geoipUser.geodata.longitude
              };
          } else {
            locationToUse = {
              latitude: geoipUser.geodata.latitude,
              longitude: geoipUser.geodata.longitude
            };
          }

          // Crear userData combinando la información
          const userData = {
            ...geoipUser,
            clientName: matchingFirebaseUser ? 
              (matchingFirebaseUser.clientName || "Usuario Externo") : 
              "Usuario Externo",
            location: locationToUse,
            ...(matchingFirebaseUser && {
              lastOnline: matchingFirebaseUser.lastOnline,
              last_changed: matchingFirebaseUser.last_changed
            })
          };

          users.push(userData);

          // Solo agregar feature si tenemos coordenadas válidas
          if (userData.location && 
              typeof userData.location.latitude === 'number' && 
              typeof userData.location.longitude === 'number') {
            const coordinates = [userData.location.longitude, userData.location.latitude];
            const feature = new Feature({
              geometry: new Point(fromLonLat(coordinates)),
              userData: userData
            });
            vectorSource.addFeature(feature);
          }
        });

        setConnectedUsers(users);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [getUsers, radioname, serverid, vectorSource]);

  const handleDownload = () => {
    try {
      // Agrupar usuarios por IP, excepto Usuario Externo
      const groupedUsers = connectedUsers.reduce((acc, user) => {
        if (user.clientName === 'Usuario Externo') {
          // Para Usuario Externo, mantener cada conexión separada
          acc.push(user);
        } else {
          // Para otros usuarios, agrupar por IP
          const existingUser = acc.find(u => u.ip === user.ip && u.clientName !== 'Usuario Externo');
          if (!existingUser) {
            acc.push(user);
          }
        }
        return acc;
      }, []);

      const csvData = groupedUsers.map(user => {
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split('T')[0];
        const formattedTime = currentDate.toTimeString().split(' ')[0];
    
        return {
          Sucursal: user.clientName,
          Ip: user.ip,
          Ciudad: user.geodata ? user.geodata.city : 'Desconocido',
          Estado: user.geodata ? user.geodata.region : 'Desconocido',
          País: user.geodata ? user.geodata.country : 'Desconocido',
          Latitud: user.location.latitude,
          Longitud: user.location.longitude,
          Fecha: formattedDate,
          Hora: formattedTime,
          Tiempo_Conectado: user.connecttime,
        };
      });

      // Generar estadísticas generales
      const uniqueIPs = new Set(groupedUsers.map(user => user.ip));
      const uniqueClients = new Set(groupedUsers.map(user => user.clientName));
      const uniqueLocations = new Set(groupedUsers
        .filter(user => user.geodata)
        .map(user => `${user.geodata.city}, ${user.geodata.region}`));
      
      const generalStats = [{
         
        'Métrica': 'IPs Únicas',
        'Valor': uniqueIPs.size
      }, {
        'Métrica': 'Clientes Únicos',
        'Valor': uniqueClients.size
      }, {
        'Métrica': 'Ubicaciones Únicas',
        'Valor': uniqueLocations.size
      }, {
        'Métrica': 'Usuarios Externos',
        'Valor': groupedUsers.filter(user => user.clientName === 'Usuario Externo').length
      }, {
        'Métrica': 'Usuarios Registrados',
        'Valor': groupedUsers.filter(user => user.clientName !== 'Usuario Externo').length
      }, {
        'Métrica': 'Países Conectados',
        'Valor': new Set(groupedUsers
          .filter(user => user.geodata?.country)
          .map(user => user.geodata.country)).size
      }, {
        'Métrica': 'Estados/Regiones',
        'Valor': new Set(groupedUsers
          .filter(user => user.geodata?.region)
          .map(user => `${user.geodata.region}, ${user.geodata.country}`)).size
      }, {
        'Métrica': 'Ciudades',
        'Valor': new Set(groupedUsers
          .filter(user => user.geodata?.city)
          .map(user => user.geodata.city)).size
      }];

      // Crear el libro de Excel
      const workbook = utils.book_new();
      
      // Agregar la hoja de conexiones
      const ws = utils.json_to_sheet(csvData);
      utils.book_append_sheet(workbook, ws, 'Conexiones');

      // Agregar la hoja de estadísticas
      const statsWs = utils.json_to_sheet(generalStats);
      utils.book_append_sheet(workbook, statsWs, 'Estadisticas');

      // Ajustar el ancho de las columnas
      const wscols = [];
      for (let i = 0; i < Object.keys(csvData[0] || {}).length; i++) {
        wscols.push({ wch: 20 }); // width in characters
      }
      ws['!cols'] = wscols;
      statsWs['!cols'] = [{ wch: 30 }, { wch: 20 }];
      
      const fileName = `conexiones_activas_${radioname}_${new Date().toISOString().split('T')[0]}.xlsx`;
      writeFile(workbook, fileName);
      
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      console.error('Error exportando datos:', error);
      toast.error('Error al exportar el reporte');
    }
  };

  const handleViewToggle = () => {
    if (showHistory) {
      if (mapElement.current) {
        mapElement.current.innerHTML = '';
      }
      mapInstance.current = null;
    }
    setShowHistory(!showHistory);
  };

  return (
    <div className="connected-users-container">
      <div className="map-header">
        <h2>Monitoreo en tiempo real: {radioname}</h2>
        <div className="map-controls">
          <div className="users-counter">
            <i className="fas fa-users"></i>
            <span>Usuarios Conectados: {new Set(connectedUsers.map(user => user.ip)).size}</span>
          </div>
          <button 
            className="toggle-view-btn"
            onClick={handleViewToggle}
          >
            {!showHistory ? (
              <>
                <i className="fas fa-table"></i>
                Ver Historial
              </>
            ) : (
              <>
                <i className="fas fa-map-marked-alt"></i>
                Ver Mapa
              </>
            )}
          </button>
          <button className="map-btn download" onClick={handleDownload}>
            <i className="fas fa-download"></i>
            Descargar Lista
          </button>
          <button className="map-btn info" onClick={() => setIsModalOpen(true)}>
            <i className="fas fa-question-circle"></i>
            Información
          </button>
        </div>
      </div>

      {!showHistory ? (
        <>
          <div ref={mapElement} className="map-container" />
          
          {selectedUser && (
            <div className="user-info-card">
              <h3>{selectedUser.clientName}</h3>
              <div className="info-content">
                <p><i className="fas fa-map-marker-alt"></i> {selectedUser.geodata?.city || 'Desconocido'}</p>
                <p><i className="fas fa-clock"></i> {selectedUser.connecttime}</p>
                <p><i className="fas fa-network-wired"></i> {selectedUser.ip}</p>
              </div>
            </div>
          )}

          <div className="map-stats">
            <div className="stats-card">
              <div className="stat-item">
                <i className="fas fa-users"></i>
                <div className="stat-info">
                  <span className="stat-value">{new Set(connectedUsers.map(user => user.ip)).size}</span>
                  <span className="stat-label">Usuarios Conectados</span>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <ConnectionHistory 
          radioName={radioname} 
          dateRange={dateRange}
        />
      )}

      <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} className="info-modal">
        <Modal.Header closeButton>
          <Modal.Title>Preguntas Frecuentes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="faq-container">
            <div className="faq-item">
              <button 
                className="faq-question"
                onClick={() => setExpandedQuestion(expandedQuestion === 1 ? null : 1)}
              >
                <i className={`fas fa-chevron-${expandedQuestion === 1 ? 'down' : 'right'}`}></i>
                ¿Si la sucursal que busco no aparece ahí que significa?
              </button>
              <div className={`faq-answer ${expandedQuestion === 1 ? 'expanded' : ''}`}>
                Puede ser que este en usuarios externos, es decir que no use el cliente web, o puede ser que no este conectada.
              </div>
            </div>

            <div className="faq-item">
              <button 
                className="faq-question"
                onClick={() => setExpandedQuestion(expandedQuestion === 2 ? null : 2)}
              >
                <i className={`fas fa-chevron-${expandedQuestion === 2 ? 'down' : 'right'}`}></i>
                ¿Si una sucursal me aparece varias veces en el Excel descargable que significa?
              </button>
              <div className={`faq-answer ${expandedQuestion === 2 ? 'expanded' : ''}`}>
                Que esta sucursal esta teniendo varias conexiones simultaneas al cliente, esto puede significar que hay un problema de internet ó que tiene varias ventanas abiertas a la vez del cliente.
              </div>
            </div>

            <div className="faq-item">
              <button 
                className="faq-question"
                onClick={() => setExpandedQuestion(expandedQuestion === 3 ? null : 3)}
              >
                <i className={`fas fa-chevron-${expandedQuestion === 3 ? 'down' : 'right'}`}></i>
                ¿Puede haber personas conectadas al link sin que necesariamente sean sucursales?
              </button>
              <div className={`faq-answer ${expandedQuestion === 3 ? 'expanded' : ''}`}>
                Si, aparecen como usuarios externos.
              </div>
            </div>

            <div className="faq-item">
              <button 
                className="faq-question"
                onClick={() => setExpandedQuestion(expandedQuestion === 4 ? null : 4)}
              >
                <i className={`fas fa-chevron-${expandedQuestion === 4 ? 'down' : 'right'}`}></i>
                ¿Como puedo saber sí se conectaron todo el dia en la sucursales ?
              </button>
              <div className={`faq-answer ${expandedQuestion === 4 ? 'expanded' : ''}`}>
                Se esta desarrollando algo para monitorear esto, pero de momento se puede saber el tiempo de conexion continuo total
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <div className="map-footer">
        <p className="disclaimer">
          <i className="fas fa-info-circle"></i>
          La geolocalización es aproximada y depende del proveedor de internet
        </p>
      </div>
    </div>
  );
};

export default ConnectedUsers;
